// The Academy is its own page, not a Dashboard tab: no sidebar, a top nav
// that mirrors policenarratives.ai (AcademyTopNav), and under it a docs
// frame in the Attio Academy style, a sticky left navigator beside a
// breadcrumbed main column, all in one centered 1440px container (the same
// frame Attio's help center uses: 18rem navigator + 3.5rem gap, ~1040px content). The window
// scrolls (not an inner pane), so the navigator and the lesson page's
// action card stick below the fixed bar. Below lg the navigator folds into a
// "Browse" panel the nav's menu button opens. ⌘K / Ctrl+K focuses whichever
// search box is on screen; Escape clears it.
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useMatch } from "react-router-dom";
import { getCourse, getLesson } from "../catalog";
import AcademyFooter from "./AcademyFooter";
import AcademyNavigator from "./AcademyNavigator";
import AcademyTopNav from "./AcademyTopNav";

export interface Crumb {
  label: string;
  to?: string;
}

const AcademyLayout: React.FC = () => {
  const location = useLocation();
  const lessonMatch = useMatch("/academy/:courseId/:lessonId");
  const courseMatch = useMatch("/academy/:courseId");
  const courseId = lessonMatch?.params.courseId ?? courseMatch?.params.courseId;
  const lessonId = lessonMatch?.params.lessonId;
  const course = courseId ? getCourse(courseId) : undefined;
  const lesson = lessonId ? getLesson(lessonId) : undefined;

  const desktopSearch = useRef<HTMLInputElement>(null);
  const mobileSearch = useRef<HTMLInputElement>(null);
  const [browseOpen, setBrowseOpen] = useState(false);

  // New page: back to the top (hash links scroll themselves afterwards).
  useEffect(() => {
    if (!location.hash) window.scrollTo?.({ top: 0 });
    setBrowseOpen(false);
  }, [location.pathname, location.hash]);

  const focusSearch = useCallback(() => {
    const desktop = desktopSearch.current;
    if (desktop && desktop.offsetParent !== null) {
      desktop.focus();
      desktop.select();
      return;
    }
    setBrowseOpen(true);
    window.setTimeout(() => {
      mobileSearch.current?.focus();
      mobileSearch.current?.select();
    }, 50);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        focusSearch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  const crumbs: Crumb[] = [{ label: "Academy", to: course ? "/academy" : undefined }];
  if (course) crumbs.push({ label: course.title, to: lesson ? `/academy/${course.id}` : undefined });
  if (lesson) crumbs.push({ label: lesson.title });

  return (
    <div className="min-h-screen flex flex-col bg-base-100 text-base-content" data-testid="academy-layout">
      <AcademyTopNav browseOpen={browseOpen} onToggleBrowse={() => setBrowseOpen((o) => !o)} />

      {/* Offset for the fixed bar */}
      <div className="flex-1 pt-16">
        {/* Mobile browse panel, opened from the bar's menu button */}
        <div id="academy-browse" className={cn("lg:hidden grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none border-b", browseOpen ? "grid-rows-[1fr] border-base-300" : "grid-rows-[0fr] border-transparent")}>
          <div className="overflow-hidden">
            <div className="max-w-[1440px] mx-auto px-6 py-4">
              <AcademyNavigator activeCourseId={courseId} activeLessonId={lessonId} searchRef={mobileSearch} onNavigate={() => setBrowseOpen(false)} />
            </div>
          </div>
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:grid lg:grid-cols-[18rem_minmax(0,1fr)] lg:gap-14">
          {/* Desktop navigator: sticks under the bar and scrolls on its own */}
          <aside className="hidden lg:block">
            <div className="sticky top-16 max-h-[calc(100vh-4rem)] overflow-y-auto py-8 pr-4 border-r border-base-300">
              <AcademyNavigator activeCourseId={courseId} activeLessonId={lessonId} searchRef={desktopSearch} />
            </div>
          </aside>

          {/* Main column */}
          <main className="min-w-0 py-8 space-y-6">
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-base-content/60 flex-wrap">
              {crumbs.map((c, i) => (
                <span key={i} className="inline-flex items-center gap-1 min-w-0">
                  {i > 0 && <ChevronRightIcon className="size-3 shrink-0" />}
                  {c.to ? (
                    <Link to={c.to} className="hover:text-blue-600 dark:hover:text-blue-300 transition-colors truncate">
                      {c.label}
                    </Link>
                  ) : (
                    <span className="text-base-content/80 truncate" aria-current="page">
                      {c.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
            <Outlet />
          </main>
        </div>
      </div>

      <AcademyFooter />
    </div>
  );
};

export default AcademyLayout;
